import React, { useState, useEffect, useMemo, ChangeEvent } from "react"
import { useTranslation, Trans } from "react-i18next"
import { User } from "@terveystalo/web-auth-onewelcome"

import {
  Button,
  Title,
  Lead,
  Select,
  TextField,
  Icon,
  Modal,
} from "@terveystalo/design-system-react-components"
import { Autocomplete } from "./components/Autocomplete"
import authClient, { authConfig } from "./UserManager"
import { config } from "./config"
import { useFormPersistence } from "./hooks/useFormPersistence"

import {
  getBackend,
  Feedback,
  AnonymousFeedback,
  FeedbackOnBehalfOfValue,
  FeedbackFeedbackTypeValue,
  FeedbackIamValue,
  WishToReceiveReplyValue,
  PreferredContactMethodValue,
  FeedbackSubjectValue,
  FeedbackServiceValue,
  ToimipaikkaData,
} from "./client"

const client = getBackend()
interface FormElement {
  value: string
}

interface FeedbackFormProps {
  user: User | null
  isLoggedIn: boolean
}

const feedbackFormInitialState: Partial<Feedback & { name?: string }> = {
  feedbackOnBehalfOf: undefined,
  onBehalfOfName: "", // Required when feedbackOnBehalfOf is "my_child" or "my_relative"
  feedbackType: undefined,
  feedbackDescription: "",
  feedbackIam: undefined,

  feedbackSubject: undefined,
  feedbackSubjectOther: "",
  feedbackService: undefined,
  feedbackEmployer: "",
  dateOfOccurrence: undefined,
  wishToReceiveReply: undefined,
  preferredContactMethod: undefined, // Required when wishToReceiveReply is "yes"
  toimipaikka: undefined,
  name: "", // For anonymous submissions
  email: "", // Required when preferredContactMethod is "email"
  phone: "", // Required when preferredContactMethod is "phone"
}

function FeedbackForm({ user: _user, isLoggedIn }: FeedbackFormProps) {
  const { t } = useTranslation()
  const { formState, setFormState, saveFormState, isRestoring } = useFormPersistence({
    formKey: "feedbackForm",
    initialState: feedbackFormInitialState,
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitMessage, setSubmitMessage] = useState("")
  const [toimipaikkaList, setToimipaikkaList] = useState<ToimipaikkaData[]>([])
  const [showApiErrorModal, setShowApiErrorModal] = useState(false)
  const [apiErrorDetails, setApiErrorDetails] = useState<string>("")
  const [files, setFiles] = useState<File[]>([])
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [showFileUploadAuthModal, setShowFileUploadAuthModal] = useState(false)
  const wrapperRef = React.useRef<HTMLDivElement>(null)
  const isAuthenticatingRef = React.useRef(false)

  // Debug logging for wrapper styles
  React.useEffect(() => {
    if (wrapperRef.current) {
      const computed = window.getComputedStyle(wrapperRef.current)
      console.log("FeedbackForm wrapper debug:", {
        width: computed.width,
        maxWidth: computed.maxWidth,
        marginLeft: computed.marginLeft,
        marginRight: computed.marginRight,
        classList: Array.from(wrapperRef.current.classList),
        viewportWidth: window.innerWidth,
      })
    }
  }, [])

  // Re-log on resize to see breakpoint changes
  React.useEffect(() => {
    const handleResize = () => {
      if (wrapperRef.current) {
        const computed = window.getComputedStyle(wrapperRef.current)
        console.log("FeedbackForm wrapper resize:", {
          width: computed.width,
          maxWidth: computed.maxWidth,
          viewportWidth: window.innerWidth,
        })
      }
    }
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  // Reset auth ref when restoration completes (after returning from authentication)
  useEffect(() => {
    if (!isRestoring) {
      isAuthenticatingRef.current = false
    }
  }, [isRestoring])

  // Listen for save-form-state event from parent (e.g., when top auth button is clicked)
  useEffect(() => {
    const handleSaveFormState = async () => {
      console.log("[FeedbackForm] Received save-form-state event, saving form state...")
      isAuthenticatingRef.current = true
      await saveFormState()
      // Dispatch event to signal that form state has been saved
      window.dispatchEvent(new CustomEvent("form-state-saved"))
    }

    window.addEventListener("save-form-state", handleSaveFormState)
    return () => {
      window.removeEventListener("save-form-state", handleSaveFormState)
    }
  }, [saveFormState])

  // Check if form has any data entered
  const hasFormData = useMemo(() => {
    return !!(
      formState.feedbackOnBehalfOf ||
      formState.onBehalfOfName ||
      formState.feedbackType ||
      formState.feedbackDescription ||
      formState.feedbackIam ||
      formState.feedbackSubject ||
      formState.feedbackSubjectOther ||
      formState.feedbackService ||
      formState.feedbackEmployer ||
      formState.dateOfOccurrence ||
      formState.wishToReceiveReply ||
      formState.preferredContactMethod ||
      formState.toimipaikka ||
      formState.name ||
      formState.email ||
      formState.phone ||
      files.length > 0
    )
  }, [formState, files])

  // Keep a ref to hasFormData so the beforeunload handler always has the current value
  const hasFormDataRef = React.useRef(hasFormData)
  useEffect(() => {
    hasFormDataRef.current = hasFormData
  }, [hasFormData])

  // Warn user when navigating away with unsaved form data
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Skip warning if we're redirecting to authentication (form state is saved)
      if (isAuthenticatingRef.current) {
        return
      }
      // Use ref to get current value, avoiding stale closure issues
      if (hasFormDataRef.current) {
        // Standard way to trigger the browser's "are you sure" dialog
        e.preventDefault()
        // Some browsers require returnValue to be set
        e.returnValue = ""
        return ""
      }
    }

    window.addEventListener("beforeunload", handleBeforeUnload)
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload)
    }
  }, []) // Empty deps - handler uses refs for current values

  // Fetch toimipaikka list on component mount
  useEffect(() => {
    const fetchToimipaikka = async () => {
      try {
        const data = await client.getToimipaikka()

        if (data.data && data.data.length > 0) {
          console.log(`✓ Loaded ${data.data.length} locations for autocomplete`)
          setToimipaikkaList(data.data)
        } else {
          console.error("✗ Received empty location list from API")
          setToimipaikkaList([])
          setApiErrorDetails("No location data received from server")
          setShowApiErrorModal(true)
        }
      } catch (error) {
        // Build detailed error message
        let errorDetails = "Unknown error"
        if (error && typeof error === "object") {
          if ("code" in error) {
            errorDetails = (error as { code?: string }).code || errorDetails
          }
          if ("message" in error) {
            const message = (error as { message?: string }).message
            if (message) errorDetails = message
          }
          if ("response" in error) {
            const response = (error as { response?: { status?: number } }).response
            if (response?.status) {
              errorDetails = `HTTP ${response.status}: ${errorDetails}`
            }
          }
        }

        console.error(`✗ Failed to fetch locations: ${errorDetails}`)
        setApiErrorDetails(errorDetails)
        setShowApiErrorModal(true)
      }
    }
    fetchToimipaikka()
  }, [])

  // Disable scrolling when modal is open
  React.useEffect(() => {
    if (submitMessage) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = ""
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = ""
    }
  }, [submitMessage])

  // Feedback on behalf of options
  const feedbackOnBehalfOfLabels: Record<FeedbackOnBehalfOfValue | "", string> = {
    "": t("feedbackOnBehalfOf.select"),
    [FeedbackOnBehalfOfValue.myself]: t("feedbackOnBehalfOf.myself"),
    [FeedbackOnBehalfOfValue.my_child]: t("feedbackOnBehalfOf.my_child"),
    [FeedbackOnBehalfOfValue.my_relative]: t("feedbackOnBehalfOf.my_relative"),
  }

  const feedbackOnBehalfOfOptions = Object.entries(feedbackOnBehalfOfLabels).map(
    ([value, label]) => ({
      label,
      value,
    }),
  )

  // Feedback type options
  const feedbackTypeLabels: Record<FeedbackFeedbackTypeValue | "", string> = {
    "": t("feedbackType.select"),
    [FeedbackFeedbackTypeValue.suggestion]: t("feedbackType.suggestion"),
    [FeedbackFeedbackTypeValue.thank_you]: t("feedbackType.thank_you"),
    [FeedbackFeedbackTypeValue.complaint]: t("feedbackType.complaint"),
  }

  const feedbackTypeOptions = Object.entries(feedbackTypeLabels).map(([value, label]) => ({
    label,
    value,
  }))

  // Customer type (I am) options
  const feedbackIamLabels: Record<FeedbackIamValue | "", string> = {
    "": t("feedbackIam.select"),
    [FeedbackIamValue.public_sector]: t("feedbackIam.public_sector"),
    [FeedbackIamValue.kela65]: t("feedbackIam.kela65"),
    [FeedbackIamValue.commitment_to_pay]: t("feedbackIam.commitment_to_pay"),
    [FeedbackIamValue.purchase_service_authorization]: t(
      "feedbackIam.purchase_service_authorization",
    ),
    [FeedbackIamValue.service_voucher]: t("feedbackIam.service_voucher"),
    [FeedbackIamValue.sickness_fund]: t("feedbackIam.sickness_fund"),
    [FeedbackIamValue.occupational_health_care]: t("feedbackIam.occupational_health_care"),
    [FeedbackIamValue.insurance]: t("feedbackIam.insurance"),
    [FeedbackIamValue.private]: t("feedbackIam.private"),
  }

  const feedbackIamOptions = Object.entries(feedbackIamLabels).map(([value, label]) => ({
    label,
    value,
  }))

  // Subject options
  const feedbackSubjectLabels: Record<FeedbackSubjectValue | "", string> = {
    "": t("feedbackSubject.select"),
    [FeedbackSubjectValue.appointment_booking]: t("feedbackSubject.appointment_booking"),
    [FeedbackSubjectValue.customer_service]: t("feedbackSubject.customer_service"),
    [FeedbackSubjectValue.arrival_to_appointment]: t("feedbackSubject.arrival_to_appointment"),
    [FeedbackSubjectValue.quality_of_care]: t("feedbackSubject.quality_of_care"),
    [FeedbackSubjectValue.examination]: t("feedbackSubject.examination"),
    [FeedbackSubjectValue.billing_or_payment]: t("feedbackSubject.billing_or_payment"),
    [FeedbackSubjectValue.pricing_or_campaigns]: t("feedbackSubject.pricing_or_campaigns"),
    [FeedbackSubjectValue.online_store]: t("feedbackSubject.online_store"),
    [FeedbackSubjectValue.data_protection_and_security]: t(
      "feedbackSubject.data_protection_and_security",
    ),
    [FeedbackSubjectValue.remote_services]: t("feedbackSubject.remote_services"),
    [FeedbackSubjectValue.premises]: t("feedbackSubject.premises"),
    [FeedbackSubjectValue.applications_or_electronic_services]: t(
      "feedbackSubject.applications_or_electronic_services",
    ),
    [FeedbackSubjectValue.marketing_and_communication]: t(
      "feedbackSubject.marketing_and_communication",
    ),
    [FeedbackSubjectValue.environmental_responsibility]: t(
      "feedbackSubject.environmental_responsibility",
    ),
    [FeedbackSubjectValue.other]: t("feedbackSubject.other"),
  }

  const feedbackSubjectOptions = Object.entries(feedbackSubjectLabels).map(([value, label]) => ({
    label,
    value,
  }))

  // Service options
  const feedbackServiceLabels: Record<
    Exclude<FeedbackServiceValue, "child_protection"> | "",
    string
  > = {
    "": t("feedbackService.select"),
    [FeedbackServiceValue.customer_service_point]: t("feedbackService.customer_service_point"),
    [FeedbackServiceValue.doctor_appointment]: t("feedbackService.doctor_appointment"),
    [FeedbackServiceValue.nurse_appointment]: t("feedbackService.nurse_appointment"),
    [FeedbackServiceValue.other_specialist_appointment]: t(
      "feedbackService.other_specialist_appointment",
    ),
    [FeedbackServiceValue.laboratory_services]: t("feedbackService.laboratory_services"),
    [FeedbackServiceValue.procedure_or_surgery]: t("feedbackService.procedure_or_surgery"),
    [FeedbackServiceValue.imaging]: t("feedbackService.imaging"),
    [FeedbackServiceValue.oral_health]: t("feedbackService.oral_health"),
    [FeedbackServiceValue.occupational_health_services]: t(
      "feedbackService.occupational_health_services",
    ),
    [FeedbackServiceValue.remote_appointment_or_digital_services]: t(
      "feedbackService.remote_appointment_or_digital_services",
    ),
    [FeedbackServiceValue.rehabilitation_services]: t("feedbackService.rehabilitation_services"),
    [FeedbackServiceValue.other_service]: t("feedbackService.other_service"),
    [FeedbackServiceValue.terveystalo_app]: t("feedbackService.terveystalo_app"),
    [FeedbackServiceValue.terveystalo_website]: t("feedbackService.terveystalo_website"),
  }

  const feedbackServiceOptions = Object.entries(feedbackServiceLabels).map(([value, label]) => ({
    label,
    value,
  }))

  // Wish to receive reply options (simplified to Yes/No)
  const wishToReceiveReplyLabels: Record<WishToReceiveReplyValue | "", string> = {
    "": t("wishToReceiveReply.select"),
    [WishToReceiveReplyValue.yes]: t("wishToReceiveReply.yes"),
    [WishToReceiveReplyValue.no]: t("wishToReceiveReply.no"),
  }

  const wishToReceiveReplyOptions = Object.entries(wishToReceiveReplyLabels).map(
    ([value, label]) => ({
      label,
      value,
    }),
  )

  // Preferred contact method options
  const preferredContactMethodLabels: Record<PreferredContactMethodValue | "", string> = {
    "": t("preferredContactMethod.select"),
    [PreferredContactMethodValue.phone]: t("preferredContactMethod.phone"),
    [PreferredContactMethodValue.email]: t("preferredContactMethod.email"),
  }

  const preferredContactMethodOptions = Object.entries(preferredContactMethodLabels).map(
    ([value, label]) => ({
      label,
      value,
    }),
  )

  // Create toimipaikka options from fetched list
  const toimipaikkaOptions = useMemo(() => {
    return [
      { label: t("form.toimipaikkaSelect"), value: "" },
      ...toimipaikkaList.map((toimipaikka) => {
        // Translate special toimipaikka names, fallback to original name if no translation exists
        const translationKey = `toimipaikkaNames.${toimipaikka.name}` as const
        const translatedName = t(translationKey, { defaultValue: toimipaikka.name })

        // Debug: Log to see what names we're getting from API
        if (
          toimipaikka.name.toLowerCase().includes("ajanvaraus") ||
          toimipaikka.name.toLowerCase().includes("digiterveys") ||
          toimipaikka.name.toLowerCase().includes("hoitajatiimit") ||
          toimipaikka.name.toLowerCase().includes("yleinen")
        ) {
          console.log(
            `[FeedbackForm] Toimipaikka: "${toimipaikka.name}" -> Translated: "${translatedName}"`,
          )
        }

        return {
          label: translatedName,
          value: toimipaikka.id,
        }
      }),
    ]
  }, [toimipaikkaList, t])

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormState((prevState) => ({
      ...prevState,
      [name]: value,
    }))
  }

  const handleSelectChange = (name: keyof Feedback) => (e: ChangeEvent) => {
    const value = (e.target as unknown as FormElement).value
    setFormState((prevState) => ({
      ...prevState,
      [name]: value === "" ? undefined : value,
    }))
  }

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFiles(Array.from(e.target.files))
    }
  }

  // Handle wish to receive reply change - show auth modal if "yes" selected and not logged in
  const handleWishToReceiveReplyChange = (e: ChangeEvent) => {
    const value = (e.target as unknown as FormElement).value
    if (value === "yes" && !isLoggedIn) {
      // Save the "yes" selection before showing auth modal so it persists after authentication
      setFormState((prevState) => ({
        ...prevState,
        wishToReceiveReply: WishToReceiveReplyValue.yes,
      }))
      // Show authentication modal
      setShowAuthModal(true)
    } else {
      setFormState((prevState) => ({
        ...prevState,
        wishToReceiveReply: value === "" ? undefined : (value as WishToReceiveReplyValue),
        // Reset preferredContactMethod when changing reply wish
        preferredContactMethod: value === "no" ? undefined : prevState.preferredContactMethod,
      }))
    }
  }

  // Handle authentication modal cancel
  const handleAuthModalCancel = () => {
    setShowAuthModal(false)
    // Reset to "no" on cancel
    setFormState((prevState) => ({
      ...prevState,
      wishToReceiveReply: WishToReceiveReplyValue.no,
      preferredContactMethod: undefined,
    }))
  }

  // Handle authentication modal authenticate
  const handleAuthModalAuthenticate = async () => {
    setShowAuthModal(false)
    // Mark that we're authenticating to bypass beforeunload warning
    isAuthenticatingRef.current = true
    // Save form state before redirect
    await saveFormState()
    // Store return URL and redirect to authentication
    sessionStorage.setItem("returnUrl", window.location.href)
    authClient.signinRedirect()
  }

  // Handle file upload button click when not authenticated
  const handleFileUploadClick = () => {
    if (!isLoggedIn) {
      setShowFileUploadAuthModal(true)
    }
  }

  // Handle file upload auth modal cancel
  const handleFileUploadAuthModalCancel = () => {
    setShowFileUploadAuthModal(false)
  }

  // Handle file upload auth modal authenticate
  const handleFileUploadAuthModalAuthenticate = async () => {
    setShowFileUploadAuthModal(false)
    // Mark that we're authenticating to bypass beforeunload warning
    isAuthenticatingRef.current = true
    // Save form state before redirect
    await saveFormState()
    // Store return URL and redirect to authentication
    sessionStorage.setItem("returnUrl", window.location.href)
    authClient.signinRedirect()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitMessage("")

    // Validation
    if (!formState.feedbackOnBehalfOf) {
      setSubmitMessage(t("error.feedbackOnBehalfOfRequired"))
      setIsSubmitting(false)
      return
    }

    // Validate onBehalfOfName when required
    if (
      (formState.feedbackOnBehalfOf === "my_child" ||
        formState.feedbackOnBehalfOf === "my_relative") &&
      (!formState.onBehalfOfName || formState.onBehalfOfName.trim().length === 0)
    ) {
      setSubmitMessage(t("error.onBehalfOfNameRequired"))
      setIsSubmitting(false)
      return
    }

    if (!formState.feedbackType) {
      setSubmitMessage(t("error.feedbackTypeRequired"))
      setIsSubmitting(false)
      return
    }

    if (!formState.feedbackSubject) {
      setSubmitMessage(t("error.subjectRequired"))
      setIsSubmitting(false)
      return
    }

    if (!formState.dateOfOccurrence) {
      setSubmitMessage(t("error.visitDateRequired"))
      setIsSubmitting(false)
      return
    }

    // Toimipaikka is required
    if (!formState.toimipaikka) {
      setSubmitMessage(t("error.toimipaikkaRequired"))
      setIsSubmitting(false)
      return
    }

    if (!formState.wishToReceiveReply) {
      setSubmitMessage(t("error.wishToReceiveReplyRequired"))
      setIsSubmitting(false)
      return
    }

    // Preferred contact method is required when requesting a reply
    if (formState.wishToReceiveReply === "yes" && !formState.preferredContactMethod) {
      setSubmitMessage(t("error.preferredContactMethodRequired"))
      setIsSubmitting(false)
      return
    }

    // Email is required when preferredContactMethod is "email"
    if (
      formState.wishToReceiveReply === "yes" &&
      formState.preferredContactMethod === "email" &&
      (!formState.email || formState.email.trim().length === 0)
    ) {
      setSubmitMessage(t("error.emailRequired"))
      setIsSubmitting(false)
      return
    }

    // Phone is required when preferredContactMethod is "phone"
    if (
      formState.wishToReceiveReply === "yes" &&
      formState.preferredContactMethod === "phone" &&
      (!formState.phone || formState.phone.trim().length === 0)
    ) {
      setSubmitMessage(t("error.phoneRequired"))
      setIsSubmitting(false)
      return
    }

    try {
      let res

      // Convert dateOfOccurrence string to Date object
      const dateOfOccurrence = formState.dateOfOccurrence
        ? new Date(formState.dateOfOccurrence)
        : new Date() // Default to current date if not provided (should not happen due to required field)

      // Check if we have files to upload
      if (files.length > 0) {
        // Use FormData for multipart/form-data upload
        const formData = new FormData()

        // Add all form fields as JSON in a single field or as individual fields
        const feedbackData = isLoggedIn
          ? (() => {
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              const { name: _name, ...data } = formState
              return { ...data, dateOfOccurrence }
            })()
          : { ...formState, dateOfOccurrence }

        // Append each form field individually
        Object.entries(feedbackData).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            formData.append(key, value instanceof Date ? value.toISOString() : String(value))
          }
        })

        // Append files
        files.forEach((file) => {
          formData.append("files", file)
        })

        // Make direct API call with FormData
        const backendUrl = config.backendHost
        const endpoint = isLoggedIn ? "/api/feedbacks" : "/api/feedbacks/anonymous"
        const user = await authClient.getUser()
        const response = await fetch(`${backendUrl}${endpoint}`, {
          method: "POST",
          body: formData,
          headers: isLoggedIn
            ? {
                Authorization: `Bearer ${user?.access_token}`,
                ...(user?.id_token ? { "X-Id-Token": user.id_token } : {}),
              }
            : {},
        })

        res = { status: response.status, data: await response.json().catch(() => ({})) }
      } else {
        // No files - use regular JSON submission
        if (isLoggedIn) {
          // Authenticated submission - exclude name field
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { name: _name, ...feedbackData } = formState
          res = await client.createFeedback({
            ...feedbackData,
            dateOfOccurrence,
          } as Feedback)
        } else {
          // Anonymous submission - include name field
          res = await client.createAnonymousFeedback({
            ...formState,
            dateOfOccurrence,
          } as AnonymousFeedback)
        }
      }

      if (res.status < 300) {
        setSubmitMessage(t("success.feedbackSubmitted"))
        // Reset form
        setFormState(feedbackFormInitialState)
        setFiles([]) // Clear files
      } else {
        setSubmitMessage(t("error.submitFailed"))
      }
    } catch (error) {
      console.error(error)
      // Check if it's a 401 authentication error
      if (error && typeof error === "object" && "response" in error) {
        const response = (error as { response?: { status?: number } }).response
        if (response?.status === 401) {
          setSubmitMessage(t("error.authenticationFailed"))
          setIsSubmitting(false)
          return
        }
      }
      setSubmitMessage(
        `${t("error.generic")}: ${error instanceof Error ? error.message : t("error.submitFailed")}`,
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  // Show loading state while restoring form data
  if (isRestoring) {
    return (
      <div className="min-h-screen bg-coal04 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-lake mx-auto mb-4"></div>
          <p className="text-coal65">{t("form.loading")}</p>
        </div>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="min-h-screen bg-coal04">
        <div ref={wrapperRef} className="w-full m:max-w-7xl m:mx-auto">
          <header className="bg-woolhead shadow-sm border-b border-coal15">
            <div className="px-4 s:px-6 m:px-8 py-2">
              <Title>{t("form.title")}</Title>
              <p className="mt-0 mb-0 text-base">
                <Trans
                  i18nKey="form.guideText"
                  components={{
                    boldItalic: <strong style={{ fontStyle: "italic" }} />,
                  }}
                />
              </p>
              <Lead>
                <Trans
                  i18nKey="form.lead"
                  components={{
                    red: <span style={{ color: "#CE0F69" }} />,
                  }}
                />
              </Lead>
            </div>
          </header>
          <main className="s:px-6 m:px-8">
            <div
              className="bg-woolhead rounded-lg shadow p-2"
              style={{ backgroundColor: "#f8f9fa" }}
            >
              <Title as="h2" size="l" className="mb-2">
                {t("form.feedbackInfo")}
              </Title>

              {/* Simple form with built-in labels */}
              <div className="space-y-2 mb-4">
                {/* Annan palautetta (I'm giving feedback) */}
                <Select
                  id="feedback-on-behalf-of"
                  name="feedbackOnBehalfOf"
                  label={t("form.givingFeedback")}
                  options={feedbackOnBehalfOfOptions}
                  value={formState.feedbackOnBehalfOf || ""}
                  onChange={handleSelectChange("feedbackOnBehalfOf")}
                  required
                />

                {/* Henkilön nimi (Name of the person) - shown when giving feedback on behalf of someone */}
                {(formState.feedbackOnBehalfOf === "my_child" ||
                  formState.feedbackOnBehalfOf === "my_relative") && (
                  <TextField
                    id="on-behalf-of-name"
                    name="onBehalfOfName"
                    label={t("form.onBehalfOfName")}
                    placeholder={t("form.onBehalfOfNamePlaceholder")}
                    value={formState.onBehalfOfName || ""}
                    onChange={handleInputChange}
                    required
                  />
                )}

                {/* Palautteeni on (My feedback is) */}
                <Select
                  id="feedback-type"
                  name="feedbackType"
                  label={t("form.myFeedback")}
                  options={feedbackTypeOptions}
                  value={formState.feedbackType || ""}
                  onChange={handleSelectChange("feedbackType")}
                  required
                />

                {/* Olen (I am) - Optional */}
                <div className="space-y-2 mb-4">
                  <Select
                    id="feedback-iam"
                    name="feedbackIam"
                    label={t("form.iam")}
                    options={feedbackIamOptions}
                    value={formState.feedbackIam || ""}
                    onChange={handleSelectChange("feedbackIam")}
                    required={false}
                  />
                </div>

                {/* Työnantaja (Employer) - Optional */}
                <div className="space-y-2 mb-4">
                  <TextField
                    id="feedback-employer"
                    name="feedbackEmployer"
                    label={t("form.employer")}
                    as="textarea"
                    value={formState.feedbackEmployer}
                    onChange={handleInputChange}
                    required={false}
                    rows={1}
                  />
                </div>

                {/* Asia koskee (The matter concerns) */}
                <div className="space-y-2 mb-4">
                  <Select
                    id="feedback-subject"
                    name="feedbackSubject"
                    label={t("form.subject")}
                    options={feedbackSubjectOptions}
                    value={formState.feedbackSubject || ""}
                    onChange={handleSelectChange("feedbackSubject")}
                    required
                  />
                </div>

                {/* Muuta, mitä (Other, what) - Optional, only show if "other" is selected */}
                {formState.feedbackSubject === "other" && (
                  <div className="space-y-2 mb-4">
                    <TextField
                      id="feedback-subject-other"
                      name="feedbackSubjectOther"
                      label={t("form.subjectOther")}
                      as="textarea"
                      value={formState.feedbackSubjectOther}
                      onChange={handleInputChange}
                      required={false}
                      rows={2}
                    />
                  </div>
                )}

                {/* Palvelu (Service) - Optional */}
                <div className="space-y-2 mb-4">
                  <Select
                    id="feedback-service"
                    name="feedbackService"
                    label={t("form.service")}
                    options={feedbackServiceOptions}
                    value={formState.feedbackService || ""}
                    onChange={handleSelectChange("feedbackService")}
                    required={false}
                  />
                </div>

                {/* Tapahtuman päivämäärä (Date of occurrence) */}
                <div className="space-y-2 mb-4">
                  <TextField
                    id="date-of-occurrence"
                    name="dateOfOccurrence"
                    type="date"
                    label={t("form.dateOfOccurrence")}
                    value={formState.dateOfOccurrence || ""}
                    onChange={handleInputChange}
                    placeholder={t("form.selectDate")}
                    required
                  />
                </div>

                {/* Toimipaikka (Location) */}
                <div className="space-y-2 mb-4">
                  <Autocomplete
                    id="toimipaikka"
                    name="toimipaikka"
                    label={t("form.toimipaikka")}
                    placeholder={t("form.toimipaikkaPlaceholder")}
                    options={toimipaikkaOptions}
                    value={formState.toimipaikka || ""}
                    onChange={(value) => setFormState({ ...formState, toimipaikka: value })}
                    required
                  />
                  <ul className="text-xs text-gray-600 mt-1 list-disc pl-5">
                    {(
                      t("form.toimipaikkaHelp", {
                        returnObjects: true,
                        appointmentBooking: t("toimipaikkaNames.Ajanvaraus ja Asiakaspalvelu"),
                        digitalHealth: t("toimipaikkaNames.Terveystalo Digiterveys"),
                        nurseTeams: t("toimipaikkaNames.Terveystalo Digiterveys Hoitajatiimit"),
                        general: t("toimipaikkaNames.Yleinen"),
                      }) as string[]
                    ).map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </div>

                <Title as="h2" size="l" className="my-6">
                  {t("form.describeMatter")}
                </Title>

                <TextField
                  id="feedback-description"
                  name="feedbackDescription"
                  label={t("form.feedback")}
                  as="textarea"
                  value={formState.feedbackDescription}
                  onChange={handleInputChange}
                  required={false}
                  rows={4}
                />

                <div className="space-y-2 mb-4">
                  <label htmlFor="file-upload" className="block text-sm font-medium text-gray-700">
                    {t("form.attachments")}
                  </label>
                  {isLoggedIn ? (
                    <>
                      <input
                        id="file-upload"
                        name="files"
                        type="file"
                        multiple
                        onChange={handleFileChange}
                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        accept="image/*,.pdf,.doc,.docx,.txt"
                      />
                      {files.length > 0 && (
                        <p className="text-sm text-gray-600 mt-1">
                          {t("form.filesSelected", { count: files.length })}
                        </p>
                      )}
                      <p className="text-xs text-gray-500 mt-1">{t("form.fileUploadHint")}</p>
                    </>
                  ) : (
                    <Button type="button" variant="secondary" onClick={handleFileUploadClick}>
                      {t("form.addAttachment")}
                    </Button>
                  )}
                </div>

                {/* Haluan vastauksen palautteeseen (I want a response to the feedback) */}
                <div className="space-y-2 mb-4">
                  <Select
                    id="wish-to-receive-reply"
                    name="wishToReceiveReply"
                    label={t("form.replyWish")}
                    options={wishToReceiveReplyOptions}
                    value={formState.wishToReceiveReply || ""}
                    onChange={handleWishToReceiveReplyChange}
                    required
                  />
                </div>

                {/* Contact information section - only show if reply is requested and user is authenticated */}
                {formState.wishToReceiveReply === "yes" && isLoggedIn && (
                  <>
                    <Title as="h2" size="l" className="my-6">
                      {t("form.contactInfo")}
                    </Title>

                    {/* Preferred contact method dropdown */}
                    <div className="space-y-2 mb-4">
                      <Select
                        id="preferred-contact-method"
                        name="preferredContactMethod"
                        label={t("form.preferredContactMethod")}
                        options={preferredContactMethodOptions}
                        value={formState.preferredContactMethod || ""}
                        onChange={handleSelectChange("preferredContactMethod")}
                        required
                      />
                    </div>

                    {/* Email field - required when preferredContactMethod is "email" */}
                    <TextField
                      id="feedback-email"
                      name="email"
                      type="email"
                      label={t("form.email")}
                      value={formState.email || ""}
                      onChange={handleInputChange}
                      required={formState.preferredContactMethod === "email"}
                      placeholder={t("form.emailPlaceholder")}
                    />

                    {/* Phone field - required when preferredContactMethod is "phone" */}
                    <TextField
                      id="feedback-phone"
                      name="phone"
                      type="tel"
                      label={t("form.phone")}
                      value={formState.phone || ""}
                      onChange={handleInputChange}
                      required={formState.preferredContactMethod === "phone"}
                      placeholder={t("form.phonePlaceholder")}
                    />
                  </>
                )}
              </div>

              <p className="text-coal65 mb-6">
                {t("form.privacyNotice")}
                <a href={t("form.privacyStatementUrl")} className="link">
                  {t("form.privacyStatement")}
                </a>
                {t("form.privacyCustomers")}
              </p>

              {/* Debugging block - only shown when DEBUG_FORM_VALUES is set */}
              {config.debugFormValues && (
                <pre className="p-4 bg-coal15 text-coal85 rounded-md my-4">
                  <code>{JSON.stringify(formState, null, 2)}</code>
                </pre>
              )}

              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? t("form.submitting") : t("submit")}
              </Button>
            </div>
          </main>
        </div>
      </div>

      {/* Success/Error Modal */}
      {submitMessage && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              backgroundColor: "#fff",
              padding: "32px",
              borderRadius: "8px",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
              maxWidth: "500px",
              textAlign: "center",
            }}
          >
            <p style={{ marginBottom: "24px", fontSize: "1.125rem" }}>{submitMessage}</p>
            {isLoggedIn ? (
              <Button
                type="button"
                onClick={() =>
                  authClient.signoutRedirect({
                    post_logout_redirect_uri: authConfig.post_logout_redirect_uri,
                  })
                }
                variant="secondary"
              >
                <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <Icon name="LogOut" color="lake" />
                  {t("logout")}
                </span>
              </Button>
            ) : (
              <Button
                type="button"
                onClick={() => (window.location.href = "https://www.terveystalo.com")}
              >
                {t("close")}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* API Error Modal */}
      {showApiErrorModal && (
        <Modal
          open={showApiErrorModal}
          onClose={() => setShowApiErrorModal(false)}
          title={t("error.apiErrorTitle")}
          closeButtonLabel={t("close")}
        >
          <div className="flex flex-col items-center text-center space-y-3">
            <p>{t("error.apiErrorMessage")}</p>
            {apiErrorDetails && (
              <div className="bg-coal04 p-3 rounded w-full">
                <div className="flex flex-col items-center gap-2">
                  <div className="text-red-600" style={{ transform: "scale(3)" }}>
                    <Icon name="AlertCircle" />
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-mono text-red-600">{apiErrorDetails}</p>
                    <p className="text-sm font-semibold">Failed to fetch Assets</p>
                  </div>
                </div>
              </div>
            )}
            <Button onClick={() => setShowApiErrorModal(false)} className="mt-2">
              {t("error.close")}
            </Button>
          </div>
        </Modal>
      )}

      {/* Authentication Required Modal */}
      {showAuthModal && (
        <Modal
          open={showAuthModal}
          onClose={handleAuthModalCancel}
          title={t("authModal.title")}
          closeButtonLabel={t("authModal.cancel")}
        >
          <div className="flex flex-col items-center text-center">
            <div style={{ transform: "scale(2)" }}>
              <Icon name="Lock" color="lake" />
            </div>
            <p className="mt-2">{t("authModal.message")}</p>
            <div className="flex gap-3 mt-2">
              <Button onClick={handleAuthModalCancel} variant="secondary">
                {t("authModal.cancel")}
              </Button>
              <Button onClick={handleAuthModalAuthenticate}>{t("authModal.authenticate")}</Button>
            </div>
          </div>
        </Modal>
      )}

      {/* File Upload Authentication Required Modal */}
      {showFileUploadAuthModal && (
        <Modal
          open={showFileUploadAuthModal}
          onClose={handleFileUploadAuthModalCancel}
          title={t("fileUploadAuthModal.title")}
          closeButtonLabel={t("fileUploadAuthModal.cancel")}
        >
          <div className="flex flex-col items-center text-center">
            <div style={{ transform: "scale(2)" }}>
              <Icon name="Lock" color="lake" />
            </div>
            <p className="mt-2">{t("fileUploadAuthModal.message")}</p>
            <div className="flex gap-3 mt-2">
              <Button onClick={handleFileUploadAuthModalCancel} variant="secondary">
                {t("fileUploadAuthModal.cancel")}
              </Button>
              <Button onClick={handleFileUploadAuthModalAuthenticate}>
                {t("fileUploadAuthModal.authenticate")}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </form>
  )
}

export default FeedbackForm
